import { useEffect, useState } from 'react'
import { JERUSALEM, geocode, location, setLocation } from '../calendar'
import type { Location } from '../calendar'
import { Modal } from '../shell/Modal'

/**
 * Where the sun sets decides when the biblical day turns (Genesis 1:5), so this is the one
 * setting the calendar cannot do without. Jerusalem is the answer when she has not chosen.
 */
export function LocationSheet({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState<Location[]>([])
  const [busy, setBusy] = useState(false)
  const current = location()

  useEffect(() => {
    const text = q.trim()
    if (text.length < 2) {
      setResults([])
      return
    }
    // The geocoder is free and keyless; wait for a pause rather than a request per keystroke.
    let alive = true
    setBusy(true)
    const id = window.setTimeout(() => {
      geocode(text)
        .then((r) => {
          if (alive) {
            setResults(r)
            setBusy(false)
          }
        })
        .catch(() => alive && setBusy(false))
    }, 350)
    return () => {
      alive = false
      window.clearTimeout(id)
    }
  }, [q])

  function useDevice() {
    navigator.geolocation?.getCurrentPosition(
      (p) => {
        setLocation({ name: 'Your location', lat: p.coords.latitude, lon: p.coords.longitude })
        onClose()
      },
      () => {},
    )
  }

  return (
    <Modal
      title="Where are you?"
      sub={`Sunset here is when the day turns. Times are for ${current.name}.`}
      icon="ph-map-pin"
      top
      onClose={onClose}
    >
      <label className="field-label" htmlFor="loc-q">
        Town or city
      </label>
      <input id="loc-q" autoFocus value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" />
      <div className="loc-results">
        {busy && <p className="cal-small">Searching</p>}
        {!busy && q.trim().length >= 2 && results.length === 0 && (
          <p className="cal-small">No place by that name was found.</p>
        )}
        {results.map((r) => (
          <button
            key={`${r.lat},${r.lon}`}
            type="button"
            className="loc-hit"
            onClick={() => {
              setLocation(r)
              onClose()
            }}
          >
            <i className="ph-bold ph-map-pin" aria-hidden="true" /> {r.name}
          </button>
        ))}
      </div>
      <div className="cal-tools">
        <button className="btn-outline" type="button" onClick={useDevice}>
          Use this device's location
        </button>
        <button
          className="btn-quiet"
          type="button"
          onClick={() => {
            setLocation(JERUSALEM)
            onClose()
          }}
        >
          I'm in Jerusalem
        </button>
      </div>
    </Modal>
  )
}
